/**
 * Activity log — grouping, labelling and pagination.
 *
 * Lifted out of renderActivity/loadActivity in public/index.html. The rendering
 * stays in the view; everything here is the part that decides WHAT is shown, and
 * it is all pure so it can be tested without a DOM.
 *
 * Day boundaries come from dates.mjs, so the log agrees with the Custom tab's
 * date presets rather than each re-deriving "today" from its own copy of the
 * offset — which is what public/index.html does, in four separate places.
 */
import { DAY_MS, estDateKey, estTime } from './dates.mjs';

export const ACT_LIMIT = 50;

export const ACT_TYPE_META = {
  budget_update:   { icon: '💰', label: 'Budget Update',   color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  campaign_toggle: { icon: '📢', label: 'Campaign Toggle', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)' },
  ad_toggle:       { icon: '🎨', label: 'Ad Toggle',       color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)' },
  payment_retry:   { icon: '💳', label: 'Payment Retry',   color: '#fbbf24', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)' },
  // Meta 🎯: a cost / bid cap set on one ad set, read back from Meta (old → new).
  bid_update:      { icon: '🎯', label: 'Cost Cap Update', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  // The ad-off watchdog: an ad switched off by someone else, and what was done about it.
  ad_off_external:  { icon: '🚨', label: 'Ad Off (Others)',   color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)' },
  ad_off_reenabled: { icon: '▶️', label: 'Ad Turned Back On', color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)' },
  ad_off_dismissed: { icon: '⏹', label: 'Ad Left Off',       color: '#a3a3a3', bg: 'rgba(163,163,163,0.12)', border: 'rgba(163,163,163,0.3)' },
};

/** The watchdog family, as one filter value the server reads as a comma list. */
export const AD_OFF_TYPES = 'ad_off_external,ad_off_reenabled,ad_off_dismissed';

const UNKNOWN_TYPE = { icon: '📝', color: '#718096', bg: 'rgba(113,128,150,0.1)', border: 'rgba(113,128,150,0.3)' };

/** Never returns undefined — an unrecognised type still has to render. */
export function actTypeMeta(type) {
  return ACT_TYPE_META[type] ?? { ...UNKNOWN_TYPE, label: type ?? 'Event' };
}

export const isToggleType = type => type === 'campaign_toggle' || type === 'ad_toggle';

/** "Today 14:32 EST" for entries from today, "2026-07-14 09:05 EST" otherwise. */
export function fmtActTs(ts, now = Date.now()) {
  const dateStr = estDateKey(ts);
  if (!dateStr) return '';
  const label = dateStr === estDateKey(now) ? 'Today' : dateStr;
  return `${label} ${estTime(ts)} EST`;
}

/**
 * Group entries into day buckets, preserving the order the server sent them.
 *
 * Returns an array rather than an object so the caller cannot accidentally
 * depend on JS key ordering, and so a date can never collide with an inherited
 * Object.prototype key.
 *
 * @returns {Array<{date: string, label: string, items: Array}>}
 */
export function groupByDay(entries, now = Date.now()) {
  const order = [];
  const buckets = new Map();

  for (const e of entries ?? []) {
    const key = estDateKey(e.ts);
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(e);
  }

  const today = estDateKey(now);
  const yesterday = estDateKey(now - DAY_MS);

  return order.map(date => ({
    date,
    label: date === today ? 'Today' : date === yesterday ? 'Yesterday' : date,
    items: buckets.get(date),
  }));
}

/**
 * Page arithmetic for the prev/next controls.
 *
 * totalPages floors at 1 so an empty log reads "Page 1 of 1" rather than the
 * "Page 1 of 0" the original produced.
 */
export function paginate(offset, total, limit = ACT_LIMIT) {
  const safeTotal = Math.max(0, Number(total) || 0);
  return {
    page: Math.floor(offset / limit) + 1,
    totalPages: Math.max(1, Math.ceil(safeTotal / limit)),
    hasPrev: offset > 0,
    hasNext: offset + limit < safeTotal,
    total: safeTotal,
  };
}

/** Query string for /api/activity, omitting empty filters. */
export function activityQuery({ offset = 0, type = '', search = '', limit = ACT_LIMIT } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (type) params.set('type', type);
  if (search) params.set('search', search);
  return params.toString();
}
